import { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Switch,
  Paper,
  FormControlLabel,
  Button,
  ButtonGroup,
  Chip,
  Collapse,
  Alert,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  ContentCopy as ContentCopyIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  KeyboardArrowUp as ArrowUpIcon,
  KeyboardArrowDown as ArrowDownIcon,
  SwapHoriz as SwapHorizIcon,
} from "@mui/icons-material";
import {
  ContentGroup,
  ContentGroupFormatting,
} from "../../../pages/playground/types";
import {
  canAddGroup,
  createContentGroup,
  getNextAvailableSlot,
  getGroupExportName,
  switchGroupKind,
} from "../../../utils/content-groups";
import {
  generateFormattingPreview,
} from "../../../utils/content-group-formatting";
import CollapsibleFormElement from "../../common/collapsible-form-element";

// ── Presets ────────────────────────────────────────────────────────────

const presetConfigs = {
  short: { phrases: { min: 1, max: 2 }, characters: { min: 20, max: 100 }, words: { min: 15, max: 20 } },
  medium: { phrases: { min: 2, max: 3 }, characters: { min: 100, max: 200 }, words: { min: 20, max: 30 } },
  long: { phrases: { min: 3, max: 5 }, characters: { min: 200, max: 400 }, words: { min: 30, max: 40 } },
};

type FmtOption = { value: string; label: string };

const PARAGRAPH_WRAP_OPTIONS: FmtOption[] = [
  { value: "", label: "None" },
  { value: "<p>", label: "<p>" },
  { value: "<div>", label: "<div>" },
  { value: "<ul>", label: "<ul>" },
];
const PHRASE_WRAP_OPTIONS: FmtOption[] = [
  { value: "", label: "None" },
  { value: "<p>", label: "<p>" },
  { value: "<li>", label: "<li>" },
  { value: "<strong>", label: "<strong>" },
  { value: "<em>", label: "<em>" },
];
const KEY_WRAP_OPTIONS: FmtOption[] = [
  { value: "", label: "None" },
  { value: "<strong>", label: "<strong>" },
  { value: "<em>", label: "<em>" },
];
const SEPARATOR_OPTIONS: FmtOption[] = [
  { value: "space", label: "Space" },
  { value: "dash", label: "- Dash" },
  { value: "\u2022", label: "\u2022 Bullet" },
  { value: "#", label: "# Hash" },
];

// ── Preview ───────────────────────────────────────────────────────────

// getClosingTag and generateFormattingPreview imported from content-group-formatting.ts

// ── Inline format picker (chips) ──────────────────────────────────────

// Legacy/canonical equivalence: existing batches stored "<b>" or "<i>". The
// option chips now show "<strong>"/"<em>" — treat the legacy form as the
// same selection so old data renders the right chip as filled.
function tagsEquivalent(a: string, b: string): boolean {
  if (a === b) return true;
  const equiv: Record<string, string> = { "<b>": "<strong>", "<i>": "<em>" };
  return equiv[a] === b || equiv[b] === a;
}

function InlineSelect({
  label, options, value, onChange, allowCustom = false, disabled = false, disabledValues,
}: {
  label: string; options: FmtOption[]; value: string;
  onChange: (v: string) => void; allowCustom?: boolean; disabled?: boolean;
  disabledValues?: Set<string>;
}) {
  const isCustom = !options.some((o) => tagsEquivalent(o.value, value)) && value !== "";
  const [showCustom, setShowCustom] = useState(isCustom);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 32, opacity: disabled ? 0.4 : 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0, whiteSpace: "nowrap" }}>
        {label}
      </Typography>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
        {options.map((o) => {
          const isOptionDisabled = disabled || !!disabledValues?.has(o.value);
          const isSelected = tagsEquivalent(value, o.value) && !showCustom;
          return (
            <Chip
              key={o.value || "__none__"}
              label={o.label}
              size="small"
              variant={isSelected ? "filled" : "outlined"}
              color={isSelected ? "primary" : "default"}
              onClick={isOptionDisabled ? undefined : () => { setShowCustom(false); onChange(o.value); }}
              sx={{
                cursor: isOptionDisabled ? "default" : "pointer",
                opacity: isOptionDisabled && !isSelected ? 0.35 : 1,
              }}
            />
          );
        })}
        {allowCustom && (
          <>
            <Chip
              label="Custom"
              size="small"
              variant={showCustom ? "filled" : "outlined"}
              color={showCustom ? "primary" : "default"}
              onClick={disabled ? undefined : () => setShowCustom(true)}
              sx={{ cursor: disabled ? "default" : "pointer" }}
            />
            {showCustom && !disabled && (
              <TextField
                size="small"
                value={isCustom ? value : ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder="e.g. <h1>"
                sx={{ width: 100, "& .MuiInputBase-root": { height: 30 } }}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

// ── Number input without spinners ─────────────────────────────────────

const noSpinnerSx = {
  width: 80,
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    WebkitAppearance: "none", margin: 0,
  },
};

// ── Content Group Card ────────────────────────────────────────────────

type ContentGroupCardProps = {
  group: ContentGroup;
  onChange: (updated: ContentGroup) => void;
  onDelete?: () => void;
  onDuplicate?: () => void;
  canDuplicate?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onSwitchKind?: () => void;
  canSwitchKind?: boolean;
  validationErrors: Record<string, string>;
  errorPrefix: string;
  isEditingBatch: boolean;
  initiallyEnabled?: boolean;
};

function ContentGroupCard({
  group, onChange, onDelete, onDuplicate, canDuplicate, onMoveUp, onMoveDown,
  onSwitchKind, canSwitchKind, validationErrors, errorPrefix, isEditingBatch,
  initiallyEnabled, initialGroup,
}: ContentGroupCardProps & { initialGroup?: ContentGroup }) {
  const [fmtOpen, setFmtOpen] = useState(false);
  const isTitle = group.kind === "title";
  // Cache the formatting preview — Collapse keeps children mounted, so without
  // memo this re-runs on every keystroke for every card (up to 31).
  const formattingPreview = useMemo(
    () => generateFormattingPreview(group.formatting),
    [group.formatting],
  );

  const setPhrase = (field: string, val: string) => {
    const n = parseInt(val, 10);
    if (isNaN(n) || n < 0) return;
    let next = n;
    // Batch-edit constraint: max* fields can only grow; min* can only shrink.
    // The Alert at the top of the form announces this; before this guard the
    // TextField accepted any value, letting users silently invalidate already
    // saved phrases (#17).
    if (isEditingBatch && initiallyEnabled && initialGroup) {
      const initial = (initialGroup.phrases as unknown as Record<string, number>)[field];
      if (typeof initial === "number") {
        if (field.startsWith("max") && next < initial) next = initial;
        if (field.startsWith("min") && next > initial) next = initial;
      }
    }
    onChange({ ...group, phrases: { ...group.phrases, [field]: next } });
  };
  const setFmt = (field: keyof ContentGroupFormatting, val: string | boolean) => {
    const updated = { ...group.formatting, [field]: val };

    // Key-value mode ON → reset bullet/separator (phraseWrapTag kept: it wraps the key)
    if (field === "keyValueMode" && val === true) {
      updated.bulletPrefix = "";
      updated.phraseSeparator = "space";
    }
    // Key-value mode OFF → reset to clean defaults
    if (field === "keyValueMode" && val === false) {
      updated.phraseSeparator = "space";
      updated.bulletPrefix = "";
    }

    onChange({ ...group, formatting: updated });
  };
  const applyPreset = (p: "short" | "medium" | "long") => {
    const c = presetConfigs[p];
    onChange({
      ...group, enabled: true,
      phrases: {
        ...group.phrases,
        minPhrases: c.phrases.min, minCharacters: c.characters.min, minWords: c.words.min,
        maxPhrases: c.phrases.max, maxCharacters: c.characters.max, maxWords: c.words.max,
      },
    });
  };

  const groupErrors = Object.entries(validationErrors)
    .filter(([k]) => k.startsWith(errorPrefix)).map(([, m]) => m);
  const hasError = groupErrors.length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5, border: "1px solid",
        borderColor: group.enabled ? "primary.light" : "divider",
        borderRadius: 1, mb: 1,
        boxShadow: hasError ? "0px 0px 0px 1px red" : "none",
      }}
    >
      {/* ─── Header ─── */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
          <TextField
            size="small"
            value={group.label}
            onChange={(e) =>
              // Strip only HTML-injection-prone chars; keep accented letters
              // (Italian copywriters use à è é ì ò ù) and basic punctuation.
              // The export column name is normalized separately by
              // normalizeFieldName, which strips non-ASCII for filesystem safety.
              onChange({
                ...group,
                label: e.target.value.replace(/[<>"'\\/]/g, ""),
              })
            }
            placeholder="Group name"
            sx={{ maxWidth: 220, "& .MuiInputBase-root": { height: 32, fontWeight: 500 } }}
          />
          <Chip
            size="small"
            label={group.kind === "title" ? "Title" : group.kind === "description" ? "Description" : "Bullet Points"}
            color={group.kind === "title" ? "default" : group.kind === "description" ? "primary" : "secondary"}
            variant="outlined"
          />
          {/* Switch kind (description ↔ bullet points) — create mode only */}
          {!isTitle && onSwitchKind && (
            <Tooltip
              title={
                group.kind === "description"
                  ? "Switch to bullet points"
                  : "Switch to description"
              }
            >
              <span>
                <IconButton
                  size="small"
                  onClick={onSwitchKind}
                  disabled={!canSwitchKind}
                >
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {!group.enabled && !isTitle && (
            <Chip size="small" label="Disabled" color="info" variant="outlined" />
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {/* Reorder arrows (not for title) */}
          {!isTitle && (
            <>
              <IconButton size="small" onClick={onMoveUp} disabled={!onMoveUp}>
                <ArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" onClick={onMoveDown} disabled={!onMoveDown}>
                <ArrowDownIcon fontSize="small" />
              </IconButton>
            </>
          )}

          {!isTitle && (
            <Switch
              checked={group.enabled}
              onChange={() => onChange({ ...group, enabled: !group.enabled })}
              disabled={isEditingBatch && !!initiallyEnabled}
              size="small"
              sx={{ ml: 0.5 }}
            />
          )}

          {onDuplicate && (
            <Tooltip title={canDuplicate ? "Duplicate block" : "No free slot for this kind"}>
              <span>
                <IconButton
                  size="small"
                  onClick={onDuplicate}
                  disabled={!canDuplicate}
                >
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {onDelete && (
            <IconButton size="small" onClick={onDelete} color="error">
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* Export hint */}
      <Typography variant="caption" color="text.secondary" sx={{ pl: 0.5 }}>
        Export column: {getGroupExportName(group)}
      </Typography>

      {/* ─── Limits ─── */}
      <Box sx={{ mt: 2 }}>
        {!isTitle && !isEditingBatch && (
          <Box sx={{ mb: 2 }}>
            <ButtonGroup size="small" variant="outlined">
              <Button disabled={!group.enabled} onClick={() => applyPreset("short")}>Short</Button>
              <Button disabled={!group.enabled} onClick={() => applyPreset("medium")}>Medium</Button>
              <Button disabled={!group.enabled} onClick={() => applyPreset("long")}>Long</Button>
            </ButtonGroup>
          </Box>
        )}

        <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "medium", mb: 1.5 }}>Phrases</Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField label="Min" type="number" size="small" disabled={!group.enabled}
                value={group.phrases.minPhrases} onChange={(e) => setPhrase("minPhrases", e.target.value)}
                sx={noSpinnerSx} />
              <Typography sx={{ mx: 1 }}>-</Typography>
              <TextField label="Max" type="number" size="small" disabled={!group.enabled}
                value={group.phrases.maxPhrases} onChange={(e) => setPhrase("maxPhrases", e.target.value)}
                sx={noSpinnerSx} />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "medium", mb: 1.5 }}>Characters</Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField label="Min" type="number" size="small" disabled={!group.enabled}
                value={group.phrases.minCharacters} onChange={(e) => setPhrase("minCharacters", e.target.value)}
                sx={noSpinnerSx} />
              <Typography sx={{ mx: 1 }}>-</Typography>
              <TextField label="Max" type="number" size="small" disabled={!group.enabled}
                value={group.phrases.maxCharacters} onChange={(e) => setPhrase("maxCharacters", e.target.value)}
                sx={noSpinnerSx} />
            </Box>
          </Box>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "medium", mb: 1.5 }}>Words</Typography>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <TextField label="Min" type="number" size="small" disabled={!group.enabled}
                value={group.phrases.minWords} onChange={(e) => setPhrase("minWords", e.target.value)}
                sx={noSpinnerSx} />
              <Typography sx={{ mx: 1 }}>-</Typography>
              <TextField label="Max" type="number" size="small" disabled={!group.enabled}
                value={group.phrases.maxWords} onChange={(e) => setPhrase("maxWords", e.target.value)}
                sx={noSpinnerSx} />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ─── Formatting (collapsible) ─── */}
      <Box sx={{ mt: 1.5 }}>
        <Box
          sx={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}
          onClick={() => setFmtOpen(!fmtOpen)}
        >
          {fmtOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 500 }}>
            Export Formatting
          </Typography>
        </Box>

        <Collapse in={fmtOpen}>
          <Box sx={{ pl: 2, mt: 1, display: "flex", flexDirection: "column", gap: 0.75, borderLeft: "2px solid", borderColor: "divider" }}>
            <InlineSelect label="Paragraph wrap" options={PARAGRAPH_WRAP_OPTIONS}
              value={group.formatting.paragraphWrapTag} onChange={(v) => setFmt("paragraphWrapTag", v)} />
            <InlineSelect label="Per phrase wrap" options={PHRASE_WRAP_OPTIONS}
              value={group.formatting.phraseWrapTag} onChange={(v) => setFmt("phraseWrapTag", v)} />
            <InlineSelect label="Separator" options={SEPARATOR_OPTIONS}
              value={group.formatting.phraseSeparator} onChange={(v) => setFmt("phraseSeparator", v)}
              disabled={group.formatting.keyValueMode} />

            {/* New line toggle */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 32, opacity: group.formatting.keyValueMode ? 0.4 : 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0, whiteSpace: "nowrap" }}>
                New line
              </Typography>
              <FormControlLabel
                control={<Switch size="small"
                  checked={group.formatting.bulletPrefix === "newline"}
                  onChange={(e) => setFmt("bulletPrefix", e.target.checked ? "newline" : "")}
                  disabled={group.formatting.keyValueMode} />}
                label={<Typography variant="body2">Each phrase on its own line</Typography>}
                sx={{ ml: 0 }}
              />
            </Box>

            {/* Key-Value mode */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 32 }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 140, flexShrink: 0, whiteSpace: "nowrap" }}>
                Key-Value
              </Typography>
              <FormControlLabel
                control={<Switch size="small" checked={group.formatting.keyValueMode}
                  onChange={(e) => setFmt("keyValueMode", e.target.checked)} />}
                label={<Typography variant="body2">Phrases in key-value pairs</Typography>}
                sx={{ ml: 0 }}
              />
              {group.formatting.keyValueMode && (group.phrases.minPhrases % 2 !== 0 || group.phrases.maxPhrases % 2 !== 0) && (
                <Typography variant="caption" color="warning.main">
                  min/max phrases should be even
                </Typography>
              )}
            </Box>

            {/* Key wrap (only when KV mode is on) */}
            {group.formatting.keyValueMode && (
              <InlineSelect label="Key wrap" options={KEY_WRAP_OPTIONS}
                value={group.formatting.keyWrapTag} onChange={(v) => setFmt("keyWrapTag", v)} />
            )}

            {/* Preview */}
            <Box sx={{ p: 1.5, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                Preview:
              </Typography>
              <Typography component="pre"
                sx={{ fontFamily: "monospace", fontSize: "0.85rem", whiteSpace: "pre-wrap", m: 0 }}>
                {formattingPreview}
              </Typography>
            </Box>
          </Box>
        </Collapse>
      </Box>

      {/* ─── Errors ─── */}
      {groupErrors.length > 0 && (
        <Box sx={{ mt: 1, pl: 1, borderLeft: "2px solid", borderColor: "error.main" }}>
          {groupErrors.map((err, i) => (
            <Typography key={i} variant="body2" color="error" sx={{ display: "block" }}>
              {"\u2022"} {err}
            </Typography>
          ))}
        </Box>
      )}
    </Paper>
  );
}

// ── Main Component ────────────────────────────────────────────────────

type ContentGroupsSettingsProps = {
  groups: ContentGroup[];
  onChange: (groups: ContentGroup[]) => void;
  validationErrors: Record<string, string>;
  isEdit?: boolean;
  isBatch?: boolean;
  initialGroups?: ContentGroup[];
};

export default function ContentGroupsSettings({
  groups, onChange, validationErrors, isEdit, isBatch, initialGroups,
}: ContentGroupsSettingsProps) {
  const isEditingBatch = !!isEdit && !!isBatch;
  const [addMenuAnchor, setAddMenuAnchor] = useState<null | HTMLElement>(null);

  const handleGroupChange = (groupId: string, updated: ContentGroup) => {
    onChange(groups.map((g) => (g.id === groupId ? updated : g)));
  };

  const handleDeleteGroup = (groupId: string) => {
    const filtered = groups.filter((g) => g.id !== groupId);
    onChange(filtered.map((g, i) => ({ ...g, sortOrder: i })));
  };

  const handleMoveGroup = (index: number, direction: "up" | "down") => {
    const sorted = [...groups].sort((a, b) => a.sortOrder - b.sortOrder);
    const target = direction === "up" ? index - 1 : index + 1;
    if (target < 0 || target >= sorted.length) return;
    const tmp = sorted[index].sortOrder;
    sorted[index] = { ...sorted[index], sortOrder: sorted[target].sortOrder };
    sorted[target] = { ...sorted[target], sortOrder: tmp };
    onChange(sorted);
  };

  const handleAddGroup = (kind: "description" | "bullet_points") => {
    setAddMenuAnchor(null);
    if (!canAddGroup(groups)) return;
    const slot = getNextAvailableSlot(groups, kind);
    if (!slot) return;
    onChange([...groups, createContentGroup(slot, kind, groups.length)]);
  };

  const handleDuplicateGroup = (groupId: string) => {
    const source = groups.find((g) => g.id === groupId);
    if (!source || source.kind === "title") return;
    if (!canAddGroup(groups)) return;
    const slot = getNextAvailableSlot(groups, source.kind);
    if (!slot) return;
    // " (copy)" suffix avoids the unique-normalized-label rule firing on save.
    const dup: ContentGroup = {
      id: slot,
      kind: source.kind,
      label: `${source.label} (copy)`,
      enabled: source.enabled,
      sortOrder: source.sortOrder + 0.5,
      phrases: { ...source.phrases },
      formatting: { ...source.formatting },
    };
    const next = [...groups, dup]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((g, i) => ({ ...g, sortOrder: i }));
    onChange(next);
  };

  const handleSwitchKind = (groupId: string) => {
    const updated = switchGroupKind(groups, groupId);
    if (updated) onChange(updated);
  };

  // Pre-compute whether a switch is possible for each kind direction
  const canSwitchToBullets =
    getNextAvailableSlot(groups, "bullet_points") !== null;
  const canSwitchToDescriptions =
    getNextAvailableSlot(groups, "description") !== null;

  const hasErrors = Object.keys(validationErrors).some((k) => k.startsWith("settings.content_groups"));
  const descCount = groups.filter((g) => g.kind === "description").length;
  const bulletCount = groups.filter((g) => g.kind === "bullet_points").length;
  const hasDescSlot = descCount < 15 && getNextAvailableSlot(groups, "description") !== null;
  const hasBulletSlot = bulletCount < 15 && getNextAvailableSlot(groups, "bullet_points") !== null;

  const sorted = [...groups].sort((a, b) => a.sortOrder - b.sortOrder);
  const originalIndexMap = new Map(groups.map((g, i) => [g.id, i]));

  return (
    <CollapsibleFormElement title="Content Groups" hasError={hasErrors}>
      {isEditingBatch && (
        <Alert severity="info" sx={{ mb: 1 }}>
          When editing a batch, you can only increase limits or enable new groups.
        </Alert>
      )}

      {sorted.map((group, index) => {
        const isTitle = group.kind === "title";
        const initialGroup = initialGroups?.find((g) => g.id === group.id);
        const initiallyEnabled = initialGroup?.enabled;
        // Only TITLE is undeletable. Other groups can be deleted unless they
        // were already enabled on a batch edit (to avoid losing saved phrases).
        const canDelete = !isTitle && !(isEditingBatch && initiallyEnabled);
        // Kind switch: only in create mode, and only if there is a free slot
        // of the target kind.
        const showSwitchKind = !isTitle && !isEditingBatch;
        const canSwitchKind =
          group.kind === "description" ? canSwitchToBullets : canSwitchToDescriptions;
        // Duplicate: only when not editing a batch (to avoid silent enable
        // changes), title cannot be duplicated, and there must be a free
        // slot of the same kind.
        const showDuplicate = !isTitle && !isEditingBatch;
        const canDuplicate =
          canAddGroup(groups) &&
          (group.kind === "description" ? hasDescSlot : hasBulletSlot);

        return (
          <ContentGroupCard
            key={group.id}
            group={group}
            onChange={(u) => handleGroupChange(group.id, u)}
            onDelete={canDelete ? () => handleDeleteGroup(group.id) : undefined}
            onDuplicate={showDuplicate ? () => handleDuplicateGroup(group.id) : undefined}
            canDuplicate={canDuplicate}
            onMoveUp={!isTitle && index > 0 ? () => handleMoveGroup(index, "up") : undefined}
            onMoveDown={!isTitle && index < sorted.length - 1 ? () => handleMoveGroup(index, "down") : undefined}
            onSwitchKind={showSwitchKind ? () => handleSwitchKind(group.id) : undefined}
            canSwitchKind={canSwitchKind}
            validationErrors={validationErrors}
            errorPrefix={`settings.content_groups.${originalIndexMap.get(group.id)}`}
            isEditingBatch={isEditingBatch}
            initiallyEnabled={initiallyEnabled}
            initialGroup={initialGroup}
          />
        );
      })}

      {(hasDescSlot || hasBulletSlot) && (
        <Box sx={{ mt: 1, display: "flex", justifyContent: "center" }}>
          <Button variant="outlined" size="small" startIcon={<AddIcon />}
            onClick={(e) => setAddMenuAnchor(e.currentTarget)}>
            Add Group
          </Button>
          <Menu anchorEl={addMenuAnchor} open={Boolean(addMenuAnchor)}
            onClose={() => setAddMenuAnchor(null)}>
            {hasDescSlot && (
              <MenuItem onClick={() => handleAddGroup("description")}>
                Description ({descCount}/15)
              </MenuItem>
            )}
            {hasBulletSlot && (
              <MenuItem onClick={() => handleAddGroup("bullet_points")}>
                Bullet Points ({bulletCount}/15)
              </MenuItem>
            )}
          </Menu>
        </Box>
      )}
    </CollapsibleFormElement>
  );
}
